import { createContext, useContext, type Dispatch, type RefObject, type SetStateAction } from "react";
import { ecucCataSelectObjTemplate, type ECUCataSelectObjectInterface } from "../../models/ECUCInterfaces";
import APIService from "../../APIs/APIService";
import type { ClusterSelectInterface } from "../../models/ClusterSelectInterfaces";
import API from "../../APIs/API";
import { TempDataInterface, tempDataRefTemplate } from "../../models/TempDataRef";
import { ECUDataInterface, ECUDataTemplate } from "../../models/ECUData";
import { ImportProcessData } from "../../models/ImportProcessData";
import { SelectOption } from "../../utilities/dropDownOptions";

/**
 * Data context interface. Contains the data state, loading state, and validation state of the app.
 */
export interface DataContextInterface {
  schema: any;
  // ECUCataSelectObj is the object that holds the catalog selection state
  ecuCataSelectObj: ECUCataSelectObjectInterface;
  setECUCataSelectObj: Dispatch<SetStateAction<ECUCataSelectObjectInterface>>;
  
  // API instance
  api: API;

  // API instance for ECUC operations
  apiService: APIService;
  
  // Form processing state, e.g., "ECUC_SELECT", "CLUSTER_SELECT", "FORM_EDIT"
  formProcessingState: string;
  setFormProcessingState: (state: string) => void;

  architectureType: string;
  setArchitectureType: (type: string) => void;
  signingAllowedModules: string[];
  setSigningAllowedModules: Dispatch<SetStateAction<string[]>>;

  ECUData: RefObject<ECUDataInterface>;
  setECUData: (updaterOrValue: ECUDataInterface | ((prevState: ECUDataInterface) => ECUDataInterface)) => void;

  dynamicSelectOptionsMap: Record<string, SelectOption[]>;
  setDynamicSelectOptionsMap: Dispatch<SetStateAction<Record<string, SelectOption[]>>>;

  clusterSelectObj: ClusterSelectInterface[];
  setClusterSelectObj: Dispatch<SetStateAction<ClusterSelectInterface[]>>;


  resetDataProviderState: () => void;
  importProcessData: ImportProcessData;
  setImportProcessData: Dispatch<SetStateAction<ImportProcessData>>;
  tempData: RefObject<TempDataInterface>;
  setTempData: (newState: any) => void;
}

// Create a new context
export const DataContext = createContext<DataContextInterface>({
  schema: null,
  ecuCataSelectObj: structuredClone(ecucCataSelectObjTemplate),
  setECUCataSelectObj: () => {},
  // Provide a dummy IPublicClientApplication or undefined for initial/default context
  api: new API(undefined as any),
  apiService: new APIService(new API(undefined as any)),
  formProcessingState: "ECUC_SELECT",
  setFormProcessingState: () => {},
  architectureType: "",
  setArchitectureType: () => {},
  signingAllowedModules: [],
  setSigningAllowedModules: () => {},
  ECUData: { current: structuredClone(ECUDataTemplate)},
  setECUData: () => {},
  dynamicSelectOptionsMap: {},
  setDynamicSelectOptionsMap: () => {},
  clusterSelectObj: [],
  setClusterSelectObj: () => {},
  resetDataProviderState: () => {},
  importProcessData: null,
  setImportProcessData: () => {},
  tempData: { current: structuredClone(tempDataRefTemplate)},
  setTempData: () => {}
});

/**
 * Custom hook to access the data context
 * @returns the data context
 */
export function useDataContext() {
  return useContext(DataContext);
}